import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { LogoMark } from '../Logo';

const PRODUCT_GROUPS = [
  {
    title: 'Individuals',
    items: [
      ['KYC verification', '/product/kyc-verification'],
      ['Selfie & liveness', '/product/selfie-liveness'],
      ['Age verification', '/product/age-verification'],
    ],
  },
  {
    title: 'Businesses',
    items: [
      ['KYB verification', '/product/kyb-verification'],
      ['AML & sanctions', '/product/aml-sanctions'],
    ],
  },
  {
    title: 'Documents',
    items: [
      ['Electronic signatures', '/product/electronic-signatures'],
      ['Document verification', '/product/document-verification'],
    ],
  },
];

const USE_CASE_GROUPS = [
  {
    title: 'Onboarding',
    items: [
      ['Customer onboarding', '/use-cases/customer-onboarding'],
      ['Business onboarding', '/use-cases/business-onboarding'],
    ],
  },
  {
    title: 'Trust & security',
    items: [
      ['Fraud prevention', '/use-cases/fraud-prevention'],
      ['Identity-linked signing', '/use-cases/identity-linked-signing'],
    ],
  },
  {
    title: 'Industries',
    items: [
      ['Financial services', '/industries/financial-services'],
      ['Crypto', '/industries/crypto'],
      ['Marketplaces', '/industries/marketplaces'],
      ['HR & staffing', '/industries/hr-staffing'],
    ],
  },
];

const RESOURCE_GROUPS = [
  {
    title: 'Resources',
    items: [
      ['Documentation', '/documentation'],
      ['Security & compliance', '/resources/security-compliance'],
      ['System status', '/system-status'],
    ],
  },
];

const DESKTOP_DROPDOWNS = [
  { id: 'product', label: 'Product', kind: 'product' },
  { id: 'use-cases', label: 'Use cases', kind: 'useCases' },
  { id: 'resources', label: 'Resources', kind: 'resources' },
];

const FOOTER_GROUPS = [
  {
    title: 'Product',
    items: [
      ['KYC verification', '/product/kyc-verification'],
      ['KYB verification', '/product/kyb-verification'],
      ['Electronic signatures', '/product/electronic-signatures'],
      ['Documentation', '/documentation'],
    ],
  },
  {
    title: 'Use cases',
    items: [
      ['Customer onboarding', '/use-cases/customer-onboarding'],
      ['Business onboarding', '/use-cases/business-onboarding'],
      ['Fraud prevention', '/use-cases/fraud-prevention'],
      ['Identity-linked signing', '/use-cases/identity-linked-signing'],
    ],
  },
  {
    title: 'Resources',
    items: [
      ['Security & compliance', '/resources/security-compliance'],
      ['System status', '/system-status'],
      ['Pricing', '/pricing'],
    ],
  },
  {
    title: 'Company',
    items: [
      ['Contact', 'mailto:support@idemgo.com?subject=idemGO%20contact'],
      ['Privacy', '/privacy'],
      ['Terms', '/terms'],
    ],
  },
];

function MarketingSeo({ title, description, path }) {
  useEffect(() => {
    const fullTitle = title || 'idemGO · Verification Infrastructure';
    const fullDescription = description || 'Privacy-first verification infrastructure with hosted onboarding flows, APIs, webhooks, audit trails, manual review tools, and document signing.';
    const url = `https://idemgo.com${path || '/'}`;
    document.title = fullTitle;
    const setMeta = (selector, attr, value) => {
      const tag = document.querySelector(selector);
      if (tag) tag.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', fullDescription);
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', fullDescription);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', fullDescription);
    setMeta('meta[name="robots"]', 'content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
  }, [title, description, path]);
  return null;
}

function DropdownGroup({ group, linkClassName = '' }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">{group.title}</p>
      <div className="grid gap-1">
        {group.items.map(([label, to]) => (
          <Link
            key={label}
            to={to}
            className={`rounded-md px-2 py-1.5 text-sm text-zinc-700 transition-colors hover:bg-zinc-50 hover:text-zinc-950 focus:bg-zinc-50 focus:text-zinc-950 focus:outline-none ${linkClassName}`}
          >
            {label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function ProductDropdown() {
  return (
    <div className="idemgo-nav-dropdown w-[500px] rounded-lg border border-zinc-200 bg-white p-5">
      <div className="grid grid-cols-3 gap-7">
        {PRODUCT_GROUPS.map((group) => (
          <DropdownGroup key={group.title} group={group} />
        ))}
      </div>
    </div>
  );
}

function UseCasesDropdown() {
  return (
    <div className="idemgo-nav-dropdown w-[560px] rounded-lg border border-zinc-200 bg-white p-5">
      <div className="grid grid-cols-3 gap-6">
        {USE_CASE_GROUPS.map((group) => (
          <DropdownGroup key={group.title} group={group} />
        ))}
      </div>
    </div>
  );
}

function ResourcesDropdown() {
  return (
    <div className="idemgo-nav-dropdown w-[270px] rounded-lg border border-zinc-200 bg-white p-5">
      <div className="grid gap-6">
        {RESOURCE_GROUPS.map((group) => (
          <DropdownGroup key={group.title} group={group} />
        ))}
      </div>
    </div>
  );
}

function DesktopDropdownContent({ kind }) {
  if (kind === 'product') return <ProductDropdown />;
  if (kind === 'useCases') return <UseCasesDropdown />;
  return <ResourcesDropdown />;
}

function DesktopNav() {
  const [activeDropdown, setActiveDropdown] = useState(null);

  const closeDropdown = () => setActiveDropdown(null);

  return (
    <nav className="relative hidden lg:flex items-center gap-1 text-sm text-zinc-600" aria-label="Primary navigation">
      {DESKTOP_DROPDOWNS.map((item) => (
        <div
          key={item.id}
          className="relative"
          onMouseEnter={() => setActiveDropdown(item.id)}
          onMouseLeave={closeDropdown}
          onFocus={() => setActiveDropdown(item.id)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) closeDropdown();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              closeDropdown();
              event.currentTarget.querySelector('button')?.focus();
            }
          }}
        >
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-950 focus:bg-zinc-50 focus:text-zinc-950 focus:outline-none"
            onClick={() => setActiveDropdown((active) => (active === item.id ? null : item.id))}
            aria-expanded={activeDropdown === item.id}
            aria-haspopup="true"
          >
            {item.label}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${activeDropdown === item.id ? 'rotate-180' : ''}`}
              strokeWidth={1.7}
              aria-hidden="true"
            />
          </button>
          <div
            className={`absolute left-1/2 top-full z-50 mt-3 -translate-x-1/2 pt-0 transition-all duration-150 ${
              activeDropdown === item.id ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
            }`}
          >
            <DesktopDropdownContent kind={item.kind} />
          </div>
        </div>
      ))}
      <Link to="/pricing" className="inline-flex h-9 items-center rounded-md px-3 text-sm text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-950 focus:bg-zinc-50 focus:text-zinc-950 focus:outline-none">
        Pricing
      </Link>
    </nav>
  );
}

function MobileSection({ label, open, onToggle, children }) {
  return (
    <div className="border-b border-zinc-100 py-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 text-left text-base font-semibold text-zinc-950"
        aria-expanded={open}
      >
        {label}
        <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.7} aria-hidden="true"/>
      </button>
      {open && <div className="pt-4">{children}</div>}
    </div>
  );
}

function MobileNavLink({ to, onClose, children, muted = false }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className={`block rounded-md px-2 py-2 text-sm transition-colors hover:bg-zinc-50 ${
        muted ? 'text-zinc-600 hover:text-zinc-950' : 'font-medium text-zinc-900'
      }`}
    >
      {children}
    </Link>
  );
}

function MobileNav({ open, onClose }) {
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    if (!open) setOpenSections({});
  }, [open]);

  const toggleSection = (section) => {
    setOpenSections((sections) => ({ ...sections, [section]: !sections[section] }));
  };

  if (!open) return null;
  return (
    <div className="fixed inset-x-0 top-16 bottom-0 z-[1000] overflow-y-auto lg:hidden border-t border-zinc-200 bg-white">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-5 bg-white">
        <nav aria-label="Mobile primary navigation">
          <MobileSection label="Product" open={openSections.product} onToggle={() => toggleSection('product')}>
            <div className="grid sm:grid-cols-2 gap-4">
              {PRODUCT_GROUPS.map((group) => (
                <div key={group.title}>
                  <p className="px-2 pb-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400">{group.title}</p>
                  {group.items.map(([label, to]) => (
                    <MobileNavLink key={label} to={to} onClose={onClose} muted>{label}</MobileNavLink>
                  ))}
                </div>
              ))}
            </div>
          </MobileSection>

          <MobileSection label="Use cases" open={openSections.useCases} onToggle={() => toggleSection('useCases')}>
            <div className="grid sm:grid-cols-2 gap-4">
              {USE_CASE_GROUPS.map((group) => (
                <div key={group.title}>
                  <p className="px-2 pb-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400">{group.title}</p>
                  {group.items.map(([label, to]) => (
                    <MobileNavLink key={label} to={to} onClose={onClose} muted>{label}</MobileNavLink>
                  ))}
                </div>
              ))}
            </div>
          </MobileSection>

          <MobileSection label="Resources" open={openSections.resources} onToggle={() => toggleSection('resources')}>
            <div className="grid gap-4">
              {RESOURCE_GROUPS.map((group) => (
                <div key={group.title}>
                  <p className="px-2 pb-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400">{group.title}</p>
                  {group.items.map(([label, to]) => (
                    <MobileNavLink key={label} to={to} onClose={onClose} muted>{label}</MobileNavLink>
                  ))}
                </div>
              ))}
            </div>
          </MobileSection>

          <MobileNavLink to="/pricing" onClose={onClose}>Pricing</MobileNavLink>
        </nav>

        <div className="mt-6 border-t border-zinc-200 pt-6">
          <Link
            to="/signup"
            onClick={onClose}
            className="inline-flex items-center justify-center h-10 px-4 bg-black text-white rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors"
          >
            Get started
          </Link>
          <Link
            to="/login"
            onClick={onClose}
            className="ml-4 inline-flex items-center h-10 text-sm text-zinc-600 hover:text-black transition-colors"
          >
            Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function MarketingShell({ children, title, description }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const updateScrolled = () => setScrolled(window.scrollY > 2);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
    return () => window.removeEventListener('scroll', updateScrolled);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-white text-zinc-900 pt-16">
      <MarketingSeo title={title} description={description} path={location.pathname}/>
      <header className={`fixed inset-x-0 top-0 z-50 border-b idemgo-home-header ${scrolled || mobileOpen ? 'idemgo-home-header-scrolled' : ''}`}>
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <LogoMark size={25} />
            <span className="font-display text-lg font-bold">idemGO</span>
          </Link>
          <DesktopNav/>
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/login" data-testid="landing-login-link"
              className="hidden sm:inline-flex text-sm px-3 py-1.5 border border-transparent hover:border-zinc-200 rounded-sm">
              Login
            </Link>
            <Link to="/signup" data-testid="landing-signup-cta"
              className="inline-flex h-9 items-center text-sm px-3.5 bg-black text-white rounded-md hover:bg-zinc-800 transition-colors shrink-0">
              Get started
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
              className="lg:hidden h-9 w-9 border border-zinc-200 rounded-sm grid place-items-center hover:border-zinc-400 shrink-0"
              aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-4 h-4" strokeWidth={1.5}/> : <Menu className="w-4 h-4" strokeWidth={1.5}/>}
            </button>
          </div>
        </div>
      </header>
      <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)}/>
      <main>{children}</main>
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-14 md:py-16">
          <div className="grid gap-10 md:grid-cols-[1.15fr_2fr] lg:grid-cols-[1.2fr_2.4fr]">
            <div className="max-w-sm">
              <Link to="/" className="inline-flex items-center gap-2">
                <LogoMark size={24} />
                <span className="font-display text-lg font-bold">idemGO</span>
              </Link>
              <p className="mt-4 text-sm leading-relaxed text-zinc-600">Modern onboarding infrastructure for digital platforms.</p>
              <p className="mt-4 text-xs text-zinc-400">Part of the idem ecosystem.</p>
            </div>
            <nav className="grid grid-cols-2 gap-8 sm:grid-cols-4" aria-label="Footer navigation">
              {FOOTER_GROUPS.map((group) => (
                <div key={group.title}>
                  <h2 className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{group.title}</h2>
                  <div className="mt-4 grid gap-2.5">
                    {group.items.map(([label, to]) => {
                      const isExternal = to.startsWith('mailto:');
                      const className = 'text-sm text-zinc-600 transition-colors hover:text-zinc-950';
                      return isExternal ? (
                        <a key={label} href={to} className={className}>{label}</a>
                      ) : (
                        <Link key={label} to={to} className={className}>{label}</Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>
          <div className="mt-12 flex flex-col gap-3 border-t border-zinc-100 pt-6 text-xs text-zinc-400 md:flex-row md:items-center md:justify-between">
            <p>idemGO</p>
            <p>© {new Date().getFullYear()} idemGO</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
